import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, catchError, debounceTime, filter, map, merge, of, switchMap } from 'rxjs';
import { HlmBtnDirective, HlmIconComponent, HlmSpinnerComponent, HlmSwitchComponent, HlmTooltipDirective, ToastService } from '../../../ui';
import { ActionRegistryService } from '../../../core/actions/action-registry.service';
import { AmieAction } from '../../../core/actions/action.model';
import { SessionService } from '../../../core/session/session.service';
import { LoggingService } from '../../../core/services/logging.service';
import { SearchGateway } from './search-gateway';
import { CardJump, STATUS_LABEL, UnifiedResultCardComponent } from './unified-result-card.component';
import { CustomerCard, EntitySearchOptions, FacetBucket, UnifiedEntityResults } from './unified-search.model';
import { RecentEntry, RecentsService } from './recents.service';
import { CardSortMode, filterCardChildren, isPhoneticOnlyMatch, matchActions, rankCards, sortCards } from './search-match';
import { detectInputType, DetectedInputType } from './input-detection';
import { CchsidMatch, detectCchsid } from './cchsid-detection';
import { CALL_CARD_COUNTDOWN_MS, CallCardState, UnifiedCallCardComponent } from './unified-call-card.component';
import {
  CALL_LOAD_ERROR_TEXT,
  CALL_NOT_AVAILABLE_TEXT,
  CallLookupResult,
  CallLookupService,
} from '../../calls/call-lookup.service';
import { CallInformation } from '../../calls/call-information.model';
import { buildCallDetailsLink } from '../../../shared/utils/call-viewer-link.util';

/** One row in the flat, keyboard-navigable result list (spans every state). */
type FlatItem =
  | { kind: 'action'; action: AmieAction }
  | { kind: 'card'; card: CustomerCard }
  | { kind: 'recent'; entry: RecentEntry }
  /** Only ever built from a `found` cchsid lookup (E9) — never while loading / not-found / error. */
  | { kind: 'call'; call: CallInformation };

/**
 * State of the cchsid lookup behind the call card (unambiguous track) or the call entry
 * (candidate track). `null` on the component = no lookup for the current query.
 * @ticket EB-4153
 */
interface CallLookupState {
  track: 'unambiguous' | 'candidate';
  /** The trimmed cchsid, verbatim (never case-changed). */
  value: string;
  status: CallCardState;
  call: CallInformation | null;
  /** False for a restored query (F14) so returning to the landing never auto-opens again. */
  autoOpen: boolean;
}

/**
 * Screen-reader status for a found unambiguous call that will auto-open (U3) — warns before the
 * navigation and names the ways to stop it (typing, ArrowUp/Down).
 * @ticket EB-4153
 */
const CALL_AUTO_OPEN_STATUS =
  `1 call found — opens automatically in ${CALL_CARD_COUNTDOWN_MS / 1000} seconds; type or press an arrow key to stop`;

const EMPTY_RESULTS: UnifiedEntityResults = {
  cards: [],
  total: 0,
  facets: { brand: [], status: [], type: [] },
};

const INPUT_TYPE_LABEL: Record<DetectedInputType, string> = {
  id: 'Customer/Contract ID',
  phone: 'Phone',
  text: 'Name / Company',
};

/** Shown when the entity search request itself failed (HTTP/network) — never rendered as "no matches". @ticket EB-4580 */
export const SEARCH_ERROR_TEXT = 'Customer search failed.';

const EMPTY_GUIDANCE: Record<DetectedInputType, string> = {
  phone: 'No matches. Try without the area/country code.',
  id: 'No matches. Check the number.',
  text: 'No matches. Try a shorter name or check the spelling.',
};

/**
 * Cap on rendered result cards. A broad query can merge into hundreds of customers (e.g.
 * "berlin" → 891); rendering them all is a heavy DOM + one brand-logo request per card and
 * makes scrolling janky. We render the top-ranked slice and nudge the user to refine (the
 * "Showing N of M" line) or fall back to the classic grid for exhaustive browsing.
 */
const MAX_RENDERED_CARDS = 50;

/** Entity search only fires from this many characters — shorter input keeps the launchpad/actions
 * view instead of querying the backend. Referenced by the input handlers, the re-run guard, and the
 * template's "keep typing" hint so the threshold lives in exactly one place. */
const MIN_QUERY_LENGTH = 3;

/** Status facet chip merged from all ES status keys sharing one display label (e.g.
 * `formerCustomer` + `formerService` → "Former"). Toggling the chip toggles every key. */
interface StatusFacetGroup {
  label: string;
  keys: string[];
  count: number;
}

/**
 * Unified global search (EB-3621 Plan 3). One hero input blends curated actions
 * (navigation/quicklinks from ActionRegistryService) with live entity search
 * (SearchGateway), plus a launchpad for the empty-query state (recents when no
 * customer is active, or the active customer's actions when one is).
 */
@Component({
  selector: 'cmt-unified-search',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmBtnDirective, HlmIconComponent, HlmSpinnerComponent, HlmSwitchComponent, HlmTooltipDirective, UnifiedCallCardComponent, UnifiedResultCardComponent],
  template: `
    <!-- NOTE: cmt-reveal is deliberately NOT on this root wrapper. Its keyframes end on a
         translateY(0) transform with fill-mode "both", so the transform lingers after the
         animation — and a transformed ANCESTOR of a position:sticky box spawns its own
         stacking context, which made the sticky search bar paint behind the results until the
         first scroll forced a repaint (the "jumps to front" glitch). The reveal now lives on
         the individual blocks (heading + results container), never over the sticky bar. -->
    <div
      class="flex flex-col"
      [class.gap-6]="!query().trim()"
      [class.gap-4]="!!query().trim()"
    >
      <!-- Screen-reader-only live region: announces search progress + result counts as they change
           (polite), so AT users get feedback without the fragile combobox/aria-activedescendant
           machinery that the composite result cards (with inline action buttons) can't cleanly support. -->
      <p class="sr-only" role="status" aria-live="polite">{{ liveStatus() }}</p>

      <!-- Heading is idle-only: once a search starts it just wastes vertical space and pushes
           the first result out of view. (The "SEARCH" eyebrow was dropped in review.) -->
      @if (!query().trim()) {
        <h2 class="cmt-reveal font-display text-2xl font-medium italic text-foreground">
          Find a customer, or jump to an action.
        </h2>
      }

      <!-- Search input with an adjacent projected action (e.g. "Classic search") beside the bar.
           Sticky so it stays reachable while scrolling results: below the titlebar on the landing
           (top-14), at the top of the scroll container in the overlay (top-0). z-30 + a solid bg
           keep it above the result cards' hover panels (z-30, DOM-later) that scroll beneath it. -->
      <div
        class="sticky z-30 flex items-center gap-3 py-2"
        [class.top-14]="!overlay()"
        [class.bg-background]="!overlay()"
        [class.top-0]="overlay()"
        [class.bg-popover]="overlay()"
      >
        <div class="relative flex-1">
        <hlm-icon
          name="lucideSearch"
          size="1.1rem"
          class="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="text"
          class="h-14 w-full rounded-full border border-border bg-card pl-16 pr-14 text-base text-foreground shadow-[0_1px_2px_rgba(20,18,30,0.04),0_8px_24px_-12px_rgba(20,18,30,0.10)] placeholder:text-muted-foreground/70 transition-all focus:border-ring focus:shadow-[0_1px_2px_rgba(20,18,30,0.04),0_16px_40px_-16px_rgba(66,72,166,0.30)] focus:outline-none focus:ring-2 focus:ring-ring/25"
          placeholder="Customer ID, company name, phone number…"
          [value]="query()"
          #searchInput
          (input)="onQueryInput($event)"
          (keydown)="onKeydown($event)"
          autocomplete="off"
          spellcheck="false"
          aria-label="Unified search"
        />
        @if (query()) {
          <button
            type="button"
            (click)="onClear()"
            class="absolute right-5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Clear search"
          >
            <hlm-icon name="lucideX" size="0.95rem" />
          </button>
        }
        </div>
        <ng-content select="[searchHeaderAction]" />
      </div>

      @if (isUnambiguousTrack()) {
        <!-- EB-4153: an unambiguous cchsid skips the customer search, so the generic advisory hint
             (and its "never limits results" tooltip) would be wrong here — approved mockup state 1. -->
        <p
          data-testid="cchsid-input-hint"
          class="-mt-3 w-max max-w-full pl-2 font-mono-feat text-sm text-muted-foreground/80"
          hlmTooltip="Looks like a CCHSID (call center handling session ID) — AMIE looks up this one call instead of searching customers."
        >
          looks like
          <span class="mx-1.5 text-muted-foreground/40">·</span>
          <span class="text-primary">CCHSID</span>
          @if (lookup()?.status === 'loading') {
            <span class="mx-1.5 text-muted-foreground/40">·</span>
            looking up the call
          }
        </p>
      } @else if (query().trim()) {
        <p
          class="-mt-3 w-max pl-2 font-mono-feat text-sm text-muted-foreground/80"
          hlmTooltip="How the search reads your input (advisory only — never limits results): a short number is treated as a Customer/Contract ID, a long or formatted number as a phone number, and text as a name/company. The detected type just boosts the matching field."
        >
          looks like
          <span class="mx-1.5 text-muted-foreground/40">·</span>
          <span class="text-primary">{{ inputTypeLabel() }}</span>
        </p>
      }

      <!-- No overflow-hidden: result cards' floating hover button panels must not be clipped.
           cmt-reveal lives here (a sibling of the sticky bar, never its ancestor) so the entrance
           fade-up can't leave a lingering transform over the sticky search input (see note above). -->
      <div class="cmt-reveal flex flex-col rounded-2xl border border-border bg-card shadow-sm" style="animation-delay:140ms">
        @if (!query().trim()) {
          <!-- ============ EMPTY STATE / LAUNCHPAD ============ -->
          @if (sessionService.customerId() === null) {
            <div class="grid grid-cols-1 gap-2 p-2 lg:grid-cols-2 lg:gap-0 lg:divide-x lg:divide-border/70">
              @if (jumpToActions().length > 0) {
                <div class="flex flex-col gap-0.5 lg:pr-3">
                  <div class="font-mono-feat px-3 pb-1 pt-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                    Jump to
                  </div>
                  @for (action of jumpToActions(); track action.id) {
                    <div
                      tabindex="0"
                      role="button"
                      [class]="rowClass(indexOf(action))"
                      (click)="activate({ kind: 'action', action })"
                      (keydown.enter)="activateFromKey($event, { kind: 'action', action })" (keydown.space)="activateFromKey($event, { kind: 'action', action })"                    >
                      <span class="grid h-8 w-8 flex-none place-items-center rounded-lg bg-primary/10 text-primary">
                        <hlm-icon [name]="action.icon" size="0.9rem" />
                      </span>
                      <div class="flex min-w-0 flex-1 flex-col">
                        <span class="truncate text-base font-medium text-foreground">{{ action.title }}</span>
                        @if (action.subtitle) {
                          <span class="truncate text-sm text-muted-foreground">{{ action.subtitle }}</span>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
              @if (recentEntries().length > 0) {
                <div class="flex flex-col gap-0.5 border-t border-border/70 pt-2 lg:border-t-0 lg:pl-3 lg:pt-0">
                  <div class="font-mono-feat px-3 pb-1 pt-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                    Recent
                  </div>
                  @for (entry of recentEntries(); track entry.kind + entry.entityId) {
                    <div
                      tabindex="0"
                      role="button"
                      [class]="rowClass(indexOf(entry))"
                      (click)="activate({ kind: 'recent', entry })"
                      (keydown.enter)="activateFromKey($event, { kind: 'recent', entry })" (keydown.space)="activateFromKey($event, { kind: 'recent', entry })"                    >
                      <span [class]="dotClass(entry.kind)">
                        <hlm-icon [name]="iconForKind(entry.kind)" size="0.9rem" />
                      </span>
                      <div class="flex min-w-0 flex-1 flex-col">
                        <span class="truncate text-base font-medium text-foreground">{{ entry.name }}</span>
                        <span class="font-mono-feat truncate text-sm text-muted-foreground">
                          {{ entry.brand }} · {{ entry.customerId }}
                        </span>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
            @if (recentEntries().length === 0 && jumpToActions().length === 0) {
              <div class="px-4 py-10 text-center text-base text-muted-foreground">
                Start typing to search customers, phone services, or user accounts.
              </div>
            }
          } @else {
            <!-- Active customer: context band + gated actions -->
            <div class="flex items-center gap-3 border-b border-border/70 px-4 py-3">
              <span class="grid h-9 w-9 flex-none place-items-center rounded-lg bg-primary/10 text-primary">
                <hlm-icon name="lucideBuilding2" size="1rem" />
              </span>
              <div class="flex min-w-0 flex-col">
                <span class="truncate font-display text-base font-semibold text-foreground">
                  {{ activeCustomerName() }}
                </span>
                <span class="font-mono-feat text-sm text-muted-foreground">
                  Customer · {{ sessionService.customerId() }}
                </span>
              </div>
            </div>
            <!-- Action filter, right under the customer band. Narrows the columns in place and
                 (unlike the hero bar) never triggers entity search, so the two-column action
                 overview survives while the user hunts for one of the many context actions. -->
            <div class="border-b border-border/70 px-4 py-2.5">
              <div class="relative">
                <hlm-icon
                  name="lucideFilter"
                  size="0.9rem"
                  class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  class="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/25"
                  placeholder="Filter actions…"
                  [value]="actionFilter()"
                  (input)="actionFilter.set($any($event.target).value)"
                  autocomplete="off"
                  spellcheck="false"
                  aria-label="Filter actions"
                />
                @if (actionFilter()) {
                  <button
                    type="button"
                    (click)="actionFilter.set('')"
                    class="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Clear action filter"
                  >
                    <hlm-icon name="lucideX" size="0.85rem" />
                  </button>
                }
              </div>
            </div>

            <!-- Split the (many) context actions into two scannable columns: "Go to" (navigation)
                 left, "Actions" (tasks) right. Collapses to one column below lg. BOTH columns are
                 ALWAYS rendered (even when the filter empties one) so the centre divider stays put
                 at 50% instead of jumping as the user filters. -->
            <div class="grid grid-cols-1 gap-2 p-2 lg:grid-cols-2 lg:gap-0 lg:divide-x lg:divide-border/70">
              <div class="flex flex-col gap-0.5 lg:pr-3">
                <div class="font-mono-feat px-3 pb-1 pt-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                  Go to
                </div>
                @for (action of navActions(); track action.id) {
                  <div
                    tabindex="0"
                    role="button"
                    [class]="rowClass(indexOf(action))"
                    (click)="activate({ kind: 'action', action })"
                    (keydown.enter)="activateFromKey($event, { kind: 'action', action })" (keydown.space)="activateFromKey($event, { kind: 'action', action })"                  >
                    <span class="grid h-8 w-8 flex-none place-items-center rounded-lg bg-primary/10 text-primary">
                      <hlm-icon [name]="action.icon" size="0.9rem" />
                    </span>
                    <div class="flex min-w-0 flex-1 flex-col">
                      <span class="truncate text-base font-medium text-foreground">{{ action.title }}</span>
                      @if (action.subtitle) {
                        <span class="truncate text-sm text-muted-foreground">{{ action.subtitle }}</span>
                      }
                    </div>
                  </div>
                } @empty {
                  <div class="px-3 py-2 text-sm text-muted-foreground/70">No matching destinations.</div>
                }
              </div>
              <div class="flex flex-col gap-0.5 border-t border-border/70 pt-2 lg:border-t-0 lg:pl-3 lg:pt-0">
                <div class="font-mono-feat px-3 pb-1 pt-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                  Actions
                </div>
                @for (action of taskActions(); track action.id) {
                  <div
                    tabindex="0"
                    role="button"
                    [class]="rowClass(indexOf(action))"
                    (click)="activate({ kind: 'action', action })"
                    (keydown.enter)="activateFromKey($event, { kind: 'action', action })" (keydown.space)="activateFromKey($event, { kind: 'action', action })"                  >
                    <span class="grid h-8 w-8 flex-none place-items-center rounded-lg bg-accent/15 text-accent">
                      <hlm-icon [name]="action.icon" size="0.9rem" />
                    </span>
                    <div class="flex min-w-0 flex-1 flex-col">
                      <span class="truncate text-base font-medium text-foreground">{{ action.title }}</span>
                      @if (action.subtitle) {
                        <span class="truncate text-sm text-muted-foreground">{{ action.subtitle }}</span>
                      }
                    </div>
                  </div>
                } @empty {
                  <div class="px-3 py-2 text-sm text-muted-foreground/70">No matching actions.</div>
                }
              </div>
            </div>
          }
        } @else {
          <!-- ============ TYPING STATE ============ -->
          <!-- Call section (EB-4153): the unambiguous track's call card in every lookup state, or
               the candidate track's call entry once the lookup found the call (a candidate miss
               stays silent). Keyed on the cchsid so every new value gets a fresh card instance —
               the card latches its stopped countdown for good. -->
          @if (callSection().length > 0) {
            <section class="flex flex-col gap-2.5 border-t border-border/70 p-3" data-testid="call-section" aria-label="Call">
              <div class="font-mono-feat px-1 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                Call
              </div>
              @for (l of callSection(); track l.value) {
                <cmt-unified-call-card
                  [state]="l.status"
                  [call]="l.call"
                  [variant]="l.track === 'unambiguous' ? 'card' : 'entry'"
                  [autoOpen]="l.autoOpen"
                  [selected]="isCallSelected()"
                  (open)="onCallOpen()"
                  (cancelled)="focusSearchInput()"
                  (retry)="onLookupRetry()"
                />
              }
            </section>
          }
          <!-- The unambiguous track shows ONLY the call card (U1): no entity results, no actions. -->
          @if (!isUnambiguousTrack()) {
          <!-- Findings (left) + matched quick-actions (right on wide screens, stacked on top when
               narrow) so the actions stay beside the results instead of pushing them down. -->
          <div [class]="matchedActions().length > 0 ? 'grid lg:grid-cols-[minmax(0,1fr)_17rem]' : 'block'">
          <div class="min-w-0">
          @if (loading()) {
            <div class="flex items-center gap-3 border-t border-border/70 p-6 text-base text-muted-foreground">
              <hlm-spinner size="18" />
              Searching…
            </div>
          } @else if (searchFailed()) {
            <!-- EB-4580: a failed request must never read as "no customers found". Filters stay
                 clearable here — the toolbar (and its escape hatch) is not rendered in this state. -->
            <div class="border-t border-border/70 p-3">
              <div role="alert" data-testid="search-error" class="flex flex-wrap items-center gap-3.5 rounded-xl border border-dashed border-border p-4 text-base text-muted-foreground">
                <span class="grid h-11 w-11 flex-none place-items-center rounded-xl bg-destructive/10 text-destructive">
                  <hlm-icon name="lucideTriangleAlert" size="1.15rem" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="font-medium text-foreground">{{ searchErrorText }}</span> Please try again.
                </span>
                @if (hasActiveFilters()) {
                  <button hlmBtn variant="ghost" size="sm" type="button" data-testid="search-error-clear-filters"
                    class="h-[44px] w-full sm:h-9 sm:w-auto" (click)="clearFiltersAfterError()">
                    Clear filters
                  </button>
                }
                <button hlmBtn variant="outline" size="sm" type="button" data-testid="search-error-retry"
                  class="h-[44px] w-full sm:h-9 sm:w-auto" (click)="retrySearch()">
                  <hlm-icon name="lucideRefreshCw" size="0.9rem" />
                  Retry
                </button>
              </div>
            </div>
          } @else if (results() !== null && (rankedCards().length > 0 || hasActiveFilters())) {
            <!-- Toolbar lives OUTSIDE the card list so a filter that yields zero cards never hides
                 the controls (and the "Clear filters" escape hatch) that produced the empty state. -->
            <div class="flex flex-col gap-3 border-t border-border/70 p-3">
              <div class="flex flex-wrap items-center gap-2 px-1">
                <hlm-switch
                  [checked]="activeOnly()"
                  (checkedChange)="setActiveOnly($event)"
                  label="Active only"
                />
                <span class="h-5 w-px flex-none self-center bg-border"></span>
                <span class="font-mono-feat text-sm uppercase tracking-[0.15em] text-muted-foreground/70">Sort</span>
                <button
                  type="button"
                  [class]="sortChipClass('relevance')"
                  [attr.aria-pressed]="sortMode() === 'relevance'"
                  (click)="sortMode.set('relevance')"
                >
                  Relevance
                </button>
                <button
                  type="button"
                  [class]="sortChipClass('status')"
                  [attr.aria-pressed]="sortMode() === 'status'"
                  (click)="sortMode.set('status')"
                >
                  Status
                </button>
                @if (topBrandFacets().length > 0 || statusFacetGroups().length > 0) {
                  <span class="h-5 w-px flex-none self-center bg-border"></span>
                }
                @for (b of topBrandFacets(); track b.key) {
                  <button
                    type="button"
                    [class]="facetChipClass(isBrandSelected(b.key))"
                    [attr.aria-pressed]="isBrandSelected(b.key)"
                    (click)="toggleBrand(b.key)"
                  >
                    {{ b.key }} · {{ b.count }}
                  </button>
                }
                @for (g of statusFacetGroups(); track g.label) {
                  <button
                    type="button"
                    [class]="facetChipClass(isStatusGroupSelected(g))"
                    [attr.aria-pressed]="isStatusGroupSelected(g)"
                    (click)="toggleStatusGroup(g)"
                  >
                    {{ g.label }} · {{ g.count }}
                  </button>
                }
                @if (hasActiveFilters()) {
                  <button
                    type="button"
                    class="ml-auto text-sm font-medium text-primary hover:underline"
                    (click)="clearFilters()"
                  >
                    Clear filters
                  </button>
                }
              </div>
              @if (rankedCards().length > 0) {
                <div class="font-mono-feat px-1 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                  Customers
                </div>
                @for (card of rankedCards(); track card.customerId) {
                  <cmt-unified-result-card
                    [card]="card"
                    [selected]="isCardSelected(card)"
                    [phoneticMatch]="cardPhoneticMatch(card)"
                    [query]="query()"
                    [currentCustomerId]="currentCustomerId()"
                    [currentUserAccountId]="currentUserAccountId()"
                    [currentPhoneServiceId]="currentPhoneServiceId()"
                    (jump)="onCardJump(card, $event)"                  />
                }
                @if (customersTruncated()) {
                  <div class="px-3 py-2 text-sm text-muted-foreground">
                    Showing the top {{ rankedCards().length }} of {{ customerCount() }} matching customers — refine your search to narrow down.
                  </div>
                } @else {
                  <div
                    class="w-max px-3 py-1 text-sm text-muted-foreground/80"
                    hlmTooltip="'Records' are the individual customer, user-account and phone-service entries that matched — they are grouped into the customer cards above, so nothing is hidden."
                  >
                    {{ customerCount() }} {{ customerCount() === 1 ? 'customer' : 'customers' }} · {{ resultsTotal() }} matching records
                  </div>
                }
              } @else {
                <div class="px-4 py-8 text-center text-base text-muted-foreground">
                  No customers match these filters.
                </div>
              }
            </div>
          } @else if (noResultsAtAll()) {
            <div class="border-t border-border/70 px-4 py-8 text-center text-base text-muted-foreground">
              {{ emptyGuidance() }}
            </div>
          } @else if (query().trim().length < minQueryLength && matchedActions().length === 0) {
            <div class="px-4 py-8 text-center text-base text-muted-foreground">
              Keep typing — entity search starts at {{ minQueryLength }} characters.
            </div>
          }
          </div>
          @if (matchedActions().length > 0) {
            <aside class="order-first flex flex-col gap-0.5 border-t border-border/70 p-2 lg:order-none lg:border-l lg:border-t-0 lg:pl-3">
              <div class="font-mono-feat px-3 pb-1 pt-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                Actions
              </div>
              @for (action of matchedActions(); track action.id) {
                <div
                  tabindex="0"
                  role="button"
                  [class]="rowClass(indexOf(action))"
                  (click)="activate({ kind: 'action', action })"
                  (keydown.enter)="activateFromKey($event, { kind: 'action', action })" (keydown.space)="activateFromKey($event, { kind: 'action', action })"                >
                  <span class="grid h-8 w-8 flex-none place-items-center rounded-lg bg-primary/10 text-primary">
                    <hlm-icon [name]="action.icon" size="0.9rem" />
                  </span>
                  <div class="flex min-w-0 flex-1 flex-col">
                    <span class="truncate text-base font-medium text-foreground">{{ action.title }}</span>
                    @if (action.subtitle) {
                      <span class="truncate text-sm text-muted-foreground">{{ action.subtitle }}</span>
                    }
                  </div>
                </div>
              }
            </aside>
          }
          </div>
          }
        }
      </div>
    </div>
  `,
})
export class UnifiedSearchComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly searchGateway = inject(SearchGateway);
  private readonly recentsService = inject(RecentsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loggingService = inject(LoggingService);
  private readonly callLookupService = inject(CallLookupService);

  protected readonly sessionService = inject(SessionService);
  protected readonly actionRegistryService = inject(ActionRegistryService);

  /** True when hosted in the global-search overlay (vs. the landing) — drives the sticky offset + bg. */
  readonly overlay = input(false);
  /** Restores a previous query on init (e.g. the landing's persisted search after navigating back). */
  readonly initialQuery = input('');
  /** Emits the current query text on every change so the host (landing) can persist it. */
  readonly queryChange = output<string>();
  /**
   * Emits whenever the user activates a result, a jump chip, a recent or a quick
   * action — i.e. the launcher has done its job. The overlay host closes on it.
   *
   * This must NOT be inferred from router events: jumping to the entity/page the
   * operator is already on is a same-URL navigation, which Angular ignores, so no
   * NavigationEnd ever fires and the overlay would stay open with the chip looking
   * dead (EB-4175).
   */
  readonly activated = output<void>();

  readonly query = signal('');
  readonly results = signal<UnifiedEntityResults | null>(null);
  /**
   * The last entity search request failed (HTTP/network) — shown as an error, never as empty.
   * @ticket EB-4580
   */
  readonly searchFailed = signal(false);
  protected readonly searchErrorText = SEARCH_ERROR_TEXT;
  readonly loading = signal(false);
  /** -1 = nothing keyboard-highlighted yet (EB-3621 Plan 7 #2 — the first result must not be
   * pre-selected after an active search or on the empty-state launchpad). The first `ArrowDown`
   * moves it to 0; `rowClass`/`isCardSelected` only render a highlight when this is `>= 0`. */
  readonly selectedIndex = signal(-1);

  /** Exposed to the template for the min-length "keep typing" hint + its guard (see MIN_QUERY_LENGTH). */
  protected readonly minQueryLength = MIN_QUERY_LENGTH;

  protected readonly activeOnly = signal(false);
  protected readonly selectedBrands = signal<Set<string>>(new Set());
  protected readonly selectedStatuses = signal<Set<string>>(new Set());
  /** Client-side card ordering — no re-query (rankedCards() re-derives when this changes). */
  protected readonly sortMode = signal<CardSortMode>('relevance');

  private readonly searchSubject = new Subject<string>();
  /** Facet-toggle re-queries skip the 200ms debounce — the user already committed to a query, a
   * chip click should feel instant (EB-3621 Plan 4). */
  private readonly immediateSearchSubject = new Subject<string>();

  /** cchsid values to look up (EB-4153) — debounced separately from the entity search. */
  private readonly lookupSubject = new Subject<string>();

  /** cchsid lookup for the current query; `null` when the query is not a cchsid (EB-4153). */
  private readonly lookupState = signal<CallLookupState | null>(null);
  /** Read-only view of {@link lookupState} (template + specs). */
  readonly lookup = this.lookupState.asReadonly();

  /** The rendered call card / entry, so typing and ArrowUp/Down can stop its countdown (U4/F17). */
  private readonly callCard = viewChild(UnifiedCallCardComponent);
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly isUnambiguousTrack = computed(() => this.lookup()?.track === 'unambiguous');

  /** The found call, if any — the only source of the `call` flat item (E9). */
  private readonly foundCall = computed(() => {
    const l = this.lookup();
    return l?.status === 'found' ? l.call : null;
  });

  /**
   * 0 or 1 lookups to render in the call section, as a list so the template can key the card
   * on the cchsid (a fresh card — and countdown — per value). Unambiguous: every state;
   * candidate: only a hit (U1 — a candidate miss is silent).
   */
  protected readonly callSection = computed<CallLookupState[]>(() => {
    const l = this.lookup();
    if (!l) return [];
    return l.track === 'unambiguous' || l.status === 'found' ? [l] : [];
  });

  readonly inputType = computed(() => detectInputType(this.query()));
  readonly inputTypeLabel = computed(() => INPUT_TYPE_LABEL[this.inputType()]);
  readonly emptyGuidance = computed(() => EMPTY_GUIDANCE[this.inputType()]);

  /**
   * Recents live in the service as a signal, so this view repaints on both update paths:
   * a same-session `add()` and the asynchronous backend profile load (a snapshot taken at
   * construction would paint an empty list on a fresh device and never recover). The signal
   * holds a stable array reference between updates, which the reference-equality lookups in
   * `indexOf()` require.
   */
  readonly recentEntries = this.recentsService.recents;

  /**
   * The URL the operator is actually on, without the query string.
   *
   * The single source of truth for "here" (PR #42 review H6). It used to be two: the
   * badges read the *session selection* while {@link navigateOrExplain} read the router,
   * and only the router half was right. `SessionService.loadCustomer` auto-selects the
   * first user account (by last name) and its first phone service on every customer load
   * — no navigation involved (`session.service.ts:88-93`, `:110-116`) — so on
   * `/13279/contracts` the session held a UA and a PS the operator had never opened, and
   * their cards were marked "You are here" while every jump chip on them worked. That is
   * the inverse of the dead chip EB-4175 fixed: the badge told the operator a working
   * chip was dead.
   */
  private readonly currentUrl = signal(this.stripQuery(this.router.url));

  /**
   * The entities the operator is currently on, as strings for id comparison in the
   * result cards ("You are here"). Derived from the route, never from selection state.
   */
  readonly currentCustomerId = computed(() => {
    const id = this.sessionService.customerId();
    if (id === null || id === undefined) return null;
    // The customer is "here" only while the URL is actually on one of its own pages —
    // not on a UA/PS sub-page, where the dead chip is the UA/PS one, not the customer.
    return this.routeSegment('customerId') === String(id) && !this.isEntitySubPage()
      ? String(id)
      : null;
  });
  readonly currentUserAccountId = computed(() => this.routeSegment('ua'));
  readonly currentPhoneServiceId = computed(() => this.routeSegment('ps'));

  private stripQuery(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  /**
   * Routes are `/:customerId`, `/:customerId/ua/:userAccountId/...` and
   * `/:customerId/ps/:phoneServiceId/...` (`app.routes.ts:65-83`).
   */
  private routeSegment(key: 'customerId' | 'ua' | 'ps'): string | null {
    const parts = this.currentUrl().split('/').filter(Boolean);
    if (key === 'customerId') return parts[0] ?? null;
    const i = parts.indexOf(key);
    return i >= 0 && parts[i + 1] ? parts[i + 1] : null;
  }

  private isEntitySubPage(): boolean {
    const parts = this.currentUrl().split('/').filter(Boolean);
    return parts.includes('ua') || parts.includes('ps');
  }

  readonly jumpToActions = computed(() =>
    this.actionRegistryService.availableActions().filter((a) => a.requires === 'none'));

  /**
   * Launchpad-scoped action filter (active-customer state). Independent of the hero `query` —
   * typing here narrows the Go-to/Actions columns IN PLACE and never triggers entity search, so
   * the user keeps the two-column action overview instead of it being replaced by customer cards.
   */
  protected readonly actionFilter = signal('');

  /** Case-insensitive substring filter over title/subtitle/keywords; preserves input order so
   * rows don't reshuffle as the user types. Empty filter → all actions. */
  private filterLaunchpadActions(actions: AmieAction[]): AmieAction[] {
    const q = this.actionFilter().trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((a) =>
      a.title.toLowerCase().includes(q) ||
      (a.subtitle ?? '').toLowerCase().includes(q) ||
      (a.keywords ?? []).some((k) => k.toLowerCase().includes(q)));
  }

  /** Active-customer launchpad, left column: navigation quicklinks ("Go to …"), filtered. */
  readonly navActions = computed(() =>
    this.filterLaunchpadActions(this.actionRegistryService.availableActions().filter((a) => a.category === 'navigation')));

  /** Active-customer launchpad, right column: task actions (edit, print, open call viewer, …), filtered. */
  readonly taskActions = computed(() =>
    this.filterLaunchpadActions(this.actionRegistryService.availableActions().filter((a) => a.category === 'action')));

  readonly matchedActions = computed(() =>
    matchActions(this.actionRegistryService.availableActions(), this.query()));

  /** All matched customers, merged + ranked + sorted (before the render cap). */
  private readonly mergedCards = computed(() => {
    const res = this.results();
    if (!res) return [];
    const ranked = rankCards(res.cards, this.query());
    // A phone-number query matches on the phone service's number — a field the EntitySection
    // doesn't surface, so `childMatches` can't see it. Don't collapse children for phone queries:
    // the backend deliberately returned exactly the phone services that matched the number.
    const collapsed = this.inputType() === 'phone' ? ranked : ranked.map((c) => filterCardChildren(c, this.query()));
    return sortCards(collapsed, this.sortMode());
  });

  /** The customer cards actually rendered (top-N cap for perf on very broad queries). */
  readonly rankedCards = computed(() => this.mergedCards().slice(0, MAX_RENDERED_CARDS));

  /** Distinct customers matched (before the render cap). */
  readonly customerCount = computed(() => this.mergedCards().length);

  /** True only when MORE customers matched than we render — the one case where results are
   * genuinely not all shown (vs. `resultsTotal()`, which counts raw records, not customers). */
  readonly customersTruncated = computed(() => this.mergedCards().length > MAX_RENDERED_CARDS);

  readonly noResultsAtAll = computed(() =>
    !this.loading() && !this.searchFailed() && this.results() !== null && this.foundCall() === null &&
    this.matchedActions().length === 0 && this.rankedCards().length === 0);

  /** ES hit total for the current results (may exceed `rankedCards().length` — used for "Show all N"). */
  readonly resultsTotal = computed(() => this.results()?.total ?? 0);

  /**
   * Screen-reader status line (rendered into an aria-live region). Empty while idle so the launchpad
   * isn't announced; "Searching…" while a query is in flight; a concise count once results land.
   * A found unambiguous call that is still due to auto-open also warns about the pending
   * navigation — based on static eligibility (autoOpen, countdown not settled), never on the
   * ticking value, so it is announced once and not every second.
   * @ticket EB-4153
   */
  readonly liveStatus = computed(() => {
    if (!this.query().trim()) return '';
    const lookup = this.lookup();
    if (lookup?.track === 'unambiguous') {
      if (lookup.status === 'loading') return 'Searching…';
      if (lookup.status === 'not-found') return CALL_NOT_AVAILABLE_TEXT;
      if (lookup.status === 'error') return CALL_LOAD_ERROR_TEXT;
      if (lookup.autoOpen && !this.callCard()?.countdownSettled()) return CALL_AUTO_OPEN_STATUS;
      return '1 call found';
    }
    if (this.loading()) return 'Searching…';
    // The error box is role="alert" and announces the failure itself.
    if (this.searchFailed()) return this.foundCall() ? '1 call found' : '';
    if (this.results() === null) return '';
    const calls = this.foundCall() ? 1 : 0;
    const customers = this.customerCount();
    const actions = this.matchedActions().length;
    if (calls === 0 && customers === 0 && actions === 0) return this.emptyGuidance();
    const parts: string[] = [];
    if (calls > 0) parts.push('1 call');
    if (customers > 0) parts.push(`${customers} ${customers === 1 ? 'customer' : 'customers'}`);
    if (actions > 0) parts.push(`${actions} ${actions === 1 ? 'action' : 'actions'}`);
    return `${parts.join(' and ')} found`;
  });

  /**
   * Top 8 brand facets by count, for the quick-filter chip bar. Counts come from the
   * gateway's ES aggregations over the full query match set (post_filter keeps them
   * stable across facet toggles) — never recomputed from the filtered `rankedCards()`.
   */
  protected readonly topBrandFacets = computed<FacetBucket[]>(() =>
    [...(this.results()?.facets.brand ?? [])].sort((a, b) => b.count - a.count).slice(0, 8));

  /**
   * Status facets grouped by DISPLAY LABEL. The ES status aggregation returns distinct keys that
   * collapse to the same label (`formerCustomer` + `formerService` → "Former", `active` + `aktiv`
   * → "Active"), which otherwise renders duplicate chips. Grouping merges them and lets one chip
   * toggle every underlying key.
   */
  protected readonly statusFacetGroups = computed<StatusFacetGroup[]>(() => {
    const groups = new Map<string, StatusFacetGroup>();
    for (const bucket of this.results()?.facets.status ?? []) {
      const label = this.statusLabel(bucket.key);
      const group = groups.get(label) ?? { label, keys: [], count: 0 };
      group.keys.push(bucket.key);
      group.count += bucket.count;
      groups.set(label, group);
    }
    return [...groups.values()];
  });

  protected readonly hasActiveFilters = computed(() =>
    this.activeOnly() || this.selectedBrands().size > 0 || this.selectedStatuses().size > 0);

  readonly activeCustomerName = computed(() => {
    const customer = this.sessionService.customer();
    return customer?.companyName || customer?.contractFullName || 'Active customer';
  });

  /** Flat, ordered list backing keyboard navigation — mirrors what's currently rendered. */
  private readonly flatItems = computed<FlatItem[]>(() => {
    const q = this.query().trim();

    if (!q) {
      if (this.sessionService.customerId() === null) {
        const jump: FlatItem[] = this.jumpToActions().map((action) => ({ kind: 'action', action }));
        const recents: FlatItem[] = this.recentEntries().map((entry) => ({ kind: 'recent', entry }));
        return [...jump, ...recents];
      }
      // Active-customer launchpad: nav column then task column — mirrors the rendered order so
      // keyboard-highlight indices line up with what the user sees.
      return [...this.navActions(), ...this.taskActions()].map((action) => ({ kind: 'action', action }));
    }

    // F15: unambiguous track = [call] only; candidate track = call → actions → cards.
    const call = this.foundCall();
    const callItems: FlatItem[] = call ? [{ kind: 'call', call }] : [];
    if (this.isUnambiguousTrack()) return callItems;

    const items: FlatItem[] = [...callItems, ...this.matchedActions().map((action): FlatItem => ({ kind: 'action', action }))];
    const cards = this.rankedCards();
    items.push(...cards.map((card): FlatItem => ({ kind: 'card', card })));
    return items;
  });

  /**
   * Row-value → flat-list index, rebuilt once whenever `flatItems()` changes. Keyed by the wrapped
   * value's object identity (each action/card/recent appears in exactly one row), so `indexOf()` is
   * an O(1) lookup instead of an O(n) scan per rendered row — the template calls it once per row on
   * every change-detection pass (the ArrowUp/Down hot path), which was O(n²).
   */
  private readonly flatIndexByValue = computed(() => {
    const map = new Map<AmieAction | CustomerCard | RecentEntry, number>();
    this.flatItems().forEach((item, index) => {
      // The call row is never looked up by value (see isCallSelected).
      if (item.kind === 'call') return;
      const value = item.kind === 'action' ? item.action : item.kind === 'card' ? item.card : item.entry;
      map.set(value, index);
    });
    return map;
  });

  constructor() {
    /* Seeds the recents signal from localStorage and kicks off the backend profile GET;
     * `recentEntries` picks up the result when it lands. */
    this.recentsService.list();

    merge(
      this.searchSubject.pipe(debounceTime(200)),
      this.immediateSearchSubject,
    ).pipe(
      switchMap((q) => this.searchGateway.search(q, this.buildOptions()).pipe(
        map((res) => ({ q, res, failed: false })),
        // EB-4580: keep the matched quick-actions usable, but flag the failure so the
        // template shows an error + Retry instead of the "no results" guidance.
        catchError(() => of({ q, res: EMPTY_RESULTS, failed: true })),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ q, res, failed }) => {
      // Guard against a stale response landing after the user shortened the
      // query below the MIN_QUERY_LENGTH threshold while the request was in flight.
      if (this.query().trim() === q) {
        this.searchFailed.set(failed);
        this.results.set(res);
        this.loading.set(false);
        this.selectedIndex.set(-1);
      }
    });

    this.lookupSubject.pipe(
      debounceTime(200),
      switchMap((value) => this.callLookupService.findCallByCchsid(value).pipe(map((res) => ({ value, res })))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ value, res }) => this.applyLookupResult(value, res));
  }

  /** Restore a persisted query (e.g. the landing's last search after navigating back) and re-run it. */
  ngOnInit(): void {
    // Keep the route-derived "here" current. The overlay outlives navigations (it closes on
    // the operator's action, not on router events — see the same-URL trap in EB-4175), so a
    // value read once at construction would go stale.
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.currentUrl.set(this.stripQuery(this.router.url)));

    const restored = this.initialQuery().trim();
    if (restored) {
      this.query.set(this.initialQuery());
      // F14: a restored cchsid never auto-opens again (landing → /calls → Back would bounce).
      const match = detectCchsid(restored);
      this.startLookup(match, false);
      if (restored.length >= MIN_QUERY_LENGTH && match.kind !== 'unambiguous') {
        this.loading.set(true);
        this.searchSubject.next(restored);
      }
    }
  }

  onQueryInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.query.set(value);
    this.queryChange.emit(value);
    this.selectedIndex.set(-1);
    const trimmed = value.trim();
    // Any input stops a running countdown for good (U4) ...
    this.callCard()?.stopCountdown();
    // ... and replaces the lookup SYNCHRONOUSLY (F13), so neither Enter nor the timer can open
    // the previous cchsid during the debounce.
    const match = detectCchsid(trimmed);
    this.startLookup(match, true);
    this.searchFailed.set(false);
    if (match.kind === 'unambiguous') {
      // U1: an unambiguous cchsid skips the entity search entirely.
      this.loading.set(false);
      this.results.set(null);
    } else if (trimmed.length >= MIN_QUERY_LENGTH) {
      this.loading.set(true);
      this.searchSubject.next(trimmed);
    } else {
      this.loading.set(false);
      this.results.set(null);
    }
  }

  onClear(): void {
    this.query.set('');
    this.queryChange.emit('');
    this.results.set(null);
    this.searchFailed.set(false);
    this.loading.set(false);
    this.selectedIndex.set(-1);
    this.actionFilter.set('');
    this.lookupState.set(null);
  }

  onKeydown(event: KeyboardEvent): void {
    const total = this.flatItems().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.callCard()?.stopCountdown(); // F17: arrowing is interaction, like typing
        if (total > 0) this.selectedIndex.update((i) => (i + 1) % total);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.callCard()?.stopCountdown(); // F17
        // `i <= 0` also covers the -1 "nothing selected" sentinel — ArrowUp from there lands
        // on the last row, matching standard combobox behavior.
        if (total > 0) this.selectedIndex.update((i) => (i <= 0 ? total - 1 : i - 1));
        break;
      case 'Enter': {
        event.preventDefault();
        const item = this.flatItems()[this.selectedIndex()];
        if (item) this.activate(item);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.onClear();
        break;
    }
  }

  /** Enter/Space activation for a keyboard-focused row. preventDefault stops Space from scrolling
   * the page (and Enter from any default) so the row behaves like a native button on both keys. */
  activateFromKey(event: Event, item: FlatItem): void {
    event.preventDefault();
    this.activate(item);
  }

  activate(item: FlatItem): void {
    switch (item.kind) {
      case 'action':
        // Quicklink actions are heterogeneous (not all are navigations), so there
        // is no navigation-ok signal to gate on — emit synchronously. This means
        // `resultType:'quicklink'` may slightly over-count vs entity selects (which
        // are nav-gated); accepted asymmetry (PR #43 review).
        this.emitSelect('quicklink');
        item.action.run();
        this.activated.emit();
        break;
      case 'card':
        // onOpenCustomer emits on its own.
        this.onOpenCustomer(item.card);
        break;
      case 'recent':
        this.recordRecent(item.entry);
        this.navigateToRecent(item.entry);
        this.activated.emit();
        break;
      case 'call':
        this.openCall(item.call);
        break;
    }
  }

  /**
   * Opens a found call in the Call Viewer (U5).
   *
   * Uses `router.navigate` directly, NOT `navigateOrExplain` (F12): that helper compares the
   * query-stripped URL, so `/calls` → `/calls?cchsid=…` would be refused as "Already on this
   * page". `search.select` is emitted only once the navigation succeeded (F7, same as entity
   * selects); `activated` fires synchronously like every other select path (EB-4175).
   * @ticket EB-4153
   */
  private openCall(call: CallInformation): void {
    // Stop the countdown FIRST: Enter / entry click can race a running countdown into a second
    // router.navigate while this one is still in flight (final review, Minor 2).
    this.callCard()?.stopCountdown();
    const link = buildCallDetailsLink(call);
    void this.router.navigate(link.commands, { queryParams: link.queryParams }).then(
      (ok) => {
        if (ok) this.emitSelect('call');
        else this.onCallNavigationFailed();
      },
      () => this.onCallNavigationFailed(),
    );
    this.activated.emit();
  }

  /**
   * The call navigation resolved `false` or rejected: tell the operator and take the card out of
   * "Opening Call Details…" (back to the stopped state), so a click or Enter can try again.
   * @ticket EB-4153
   */
  private onCallNavigationFailed(): void {
    this.explainNavigationFailure();
    this.callCard()?.markOpenFailed();
  }

  /** Card `open` output (card click, countdown expiry, entry click) — same path as Enter on the call row. */
  protected onCallOpen(): void {
    const call = this.foundCall();
    if (call) this.activate({ kind: 'call', call });
  }

  /** Card `cancelled` output — keeps keyboard focus in the search so Enter still opens the call (TC-3). */
  protected focusSearchInput(): void {
    this.searchInput()?.nativeElement.focus();
  }

  /**
   * Card `retry` output (error state) — re-issues the lookup for the same cchsid. Focus goes back
   * to the search input: the Retry button is destroyed when the state turns `loading`, which would
   * otherwise drop keyboard focus to <body>.
   * @ticket EB-4153
   */
  protected onLookupRetry(): void {
    const l = this.lookup();
    if (!l) return;
    this.focusSearchInput();
    this.lookupState.set({ ...l, status: 'loading', call: null });
    this.lookupSubject.next(l.value);
  }

  /** Whether the call row is the keyboard-highlighted one (drives the card's `selected`). */
  protected isCallSelected(): boolean {
    const index = this.selectedIndex();
    return index >= 0 && this.flatItems()[index]?.kind === 'call';
  }

  /**
   * Replaces the lookup state for a new query: `none` clears it, a cchsid match starts a
   * fresh `loading` lookup. An unchanged value (e.g. a trailing space was typed) keeps the
   * current state — same cchsid, same call — instead of re-fetching.
   * @ticket EB-4153
   */
  private startLookup(match: CchsidMatch, autoOpen: boolean): void {
    if (match.kind === 'none') {
      this.lookupState.set(null);
      return;
    }
    const current = this.lookup();
    if (current && current.value === match.value && current.track === match.kind) {
      // Same cchsid: keep the state, but restore the U6 pre-select that onQueryInput just reset.
      if (current.track === 'unambiguous' && current.status === 'found') this.selectedIndex.set(0);
      return;
    }
    this.lookupState.set({ track: match.kind, value: match.value, status: 'loading', call: null, autoOpen });
    this.lookupSubject.next(match.value);
  }

  /**
   * Applies a lookup response; dropped when the query moved on meanwhile (stale-response guard).
   *
   * An unambiguous hit pre-selects the call card (U6/F16). That is a DELIBERATE exception to
   * "the first result is not pre-selected" (EB-3621 Plan 7 #2, see `selectedIndex`) — unambiguous
   * track only; the ES subscriber's reset to -1 never runs there because no entity search is
   * issued. Focus stays in the search input: the card is highlighted via `selected`, not focused.
   * A late candidate entry is prepended, so an existing highlight shifts by one (F15).
   * @ticket EB-4153
   */
  private applyLookupResult(value: string, res: CallLookupResult): void {
    const current = this.lookup();
    if (!current || current.value !== value || this.query().trim() !== value) return;
    this.lookupState.set({ ...current, status: res.status, call: res.status === 'found' ? res.call : null });
    if (res.status !== 'found') return;
    if (current.track === 'unambiguous') {
      // U6/F16 pre-select — see the method doc.
      this.selectedIndex.set(0);
    } else if (this.selectedIndex() >= 0) {
      // F15: the candidate entry is prepended — keep the highlight on the same row.
      this.selectedIndex.update((i) => i + 1);
    }
  }

  /** Persist a recent. `RecentsService.add()` updates the service signal, so `recentEntries`
   * repaints on its own — no re-snapshot needed. */
  private recordRecent(entry: RecentEntry): void {
    this.recentsService.add(entry);
  }

  /** Whether `card` is the current keyboard-highlighted row (drives the card's selected-ring state).
   * `-1` (nothing selected) never matches, even if `indexOf` were to also return `-1`. */
  isCardSelected(card: CustomerCard): boolean {
    const index = this.selectedIndex();
    return index >= 0 && this.indexOf(card) === index;
  }

  /** Whether `card`'s name only matched the query phonetically (not as a substring) — drives the card's "sounds alike" marker. */
  cardPhoneticMatch(card: CustomerCard): boolean {
    return isPhoneticOnlyMatch(card, this.query());
  }

  /** Card body click (or Enter on the flat-list "card" item) — opens the customer, recording a recent. */
  onOpenCustomer(card: CustomerCard): void {
    // Both the recent and search.select are recorded only once the navigation
    // actually happened — a permission-rejected, cancelled, or same-URL jump
    // must leave neither a recent for a page never opened nor a select event
    // (same rationale + shape as onCardJump; re-review defect 4 / PR #43 review).
    void this.navigateOrExplain(['/', card.customerId, 'information']).then((ok) => {
      if (ok) {
        this.emitSelect('customer');
        this.recordRecent({
          kind: 'customer', entityId: card.customerId,
          customerId: card.customerId, name: card.name, brand: card.brand, at: 0,
        });
      }
    });
    this.activated.emit();
  }

  /** A result card's jump-chip (customer / UA / PS) or card-body click — records a recent then
   * navigates to the chip's own resolved route, whichever entity/page it targets. */
  onCardJump(card: CustomerCard, j: CardJump): void {
    // Both search.select and the recent are recorded only once the navigation
    // actually happened: a cancelled or rejected one used to leave a "recent"
    // entry (and now would count a select) for a page the operator was never
    // allowed to open (re-review defect 4; search.select added PR #43 review).
    // The same-URL case resolves false too, and correctly records nothing — the
    // entry is already there from the visit that put them on the page.
    void this.navigateOrExplain(j.commands).then((ok) => {
      if (ok) {
        this.emitSelect(this.resultTypeForKind(j.kind));
        this.recordRecent({
          kind: j.kind, entityId: j.entityId,
          customerId: card.customerId, name: card.name, brand: card.brand, at: 0,
        });
      }
    });
    this.activated.emit();
  }

  /** Index of a rendered row within the current flat list (drives selection highlight); -1 if absent. */
  indexOf(value: AmieAction | CustomerCard | RecentEntry): number {
    return this.flatIndexByValue().get(value) ?? -1;
  }

  rowClass(index: number): string {
    const base = 'group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors';
    const selected = this.selectedIndex();
    const active = selected >= 0 && index === selected;
    return `${base} ${active ? 'bg-primary/10' : 'hover:bg-muted'}`;
  }

  dotClass(kind: RecentEntry['kind']): string {
    const base = 'grid h-8 w-8 flex-none place-items-center rounded-lg';
    if (kind === 'useraccount') return `${base} bg-accent/15 text-accent`;
    if (kind === 'phoneservice') return `${base} bg-success/15 text-success`;
    return `${base} bg-primary/10 text-primary`;
  }

  iconForKind(kind: RecentEntry['kind']): string {
    if (kind === 'useraccount') return 'lucideUserRound';
    if (kind === 'phoneservice') return 'lucidePhone';
    return 'lucideBuilding2';
  }

  private navigateToRecent(entry: RecentEntry): void {
    const commands: (string | number)[] =
      entry.kind === 'useraccount'
        ? ['/', entry.customerId, 'ua', entry.entityId, 'account-details']
        : entry.kind === 'phoneservice'
          ? ['/', entry.customerId, 'ps', entry.entityId, 'settings']
          : ['/', entry.customerId, 'information'];
    // Emit search.select only on a successful navigation (PR #43 review).
    void this.navigateOrExplain(commands).then((ok) => {
      if (ok) this.emitSelect(this.resultTypeForKind(entry.kind));
    });
  }

  /**
   * @ticket EB-3798
   * Usage: a unified-search result was chosen and navigation follows. Emitted
   * from every select path — customer/card jump, recent, quicklink action, and a
   * cchsid call (`'call'`, EB-4153).
   */
  private emitSelect(resultType: 'customer' | 'ua' | 'ps' | 'quicklink' | 'call'): void {
    this.loggingService.usage('search.select', { resultType });
  }

  /** Map an entity kind (card jump / recent) to the `search.select` resultType. */
  private resultTypeForKind(kind: RecentEntry['kind']): 'customer' | 'ua' | 'ps' {
    if (kind === 'useraccount') return 'ua';
    if (kind === 'phoneservice') return 'ps';
    return 'customer';
  }

  private buildOptions(): EntitySearchOptions {
    return {
      activeOnly: this.activeOnly(),
      brands: [...this.selectedBrands()],
      statuses: [...this.selectedStatuses()],
    };
  }

  /** Re-runs the CURRENT query (no debounce) with the CURRENT filter signals — called after any
   * facet/active-only toggle. Counts stay stable (post_filter over the full match set); only the
   * card list changes. No-ops below the MIN_QUERY_LENGTH search threshold. */
  private rerunCurrentQuery(): void {
    const q = this.query().trim();
    if (q.length >= MIN_QUERY_LENGTH) {
      this.loading.set(true);
      this.immediateSearchSubject.next(q);
    }
  }

  /**
   * Retry button of the search error state — re-runs the current query immediately. Focus goes
   * back to the search input (same reason as `onLookupRetry`: the button is destroyed when the
   * state turns `loading`).
   * @ticket EB-4580
   */
  protected retrySearch(): void {
    this.focusSearchInput();
    this.rerunCurrentQuery();
  }

  protected setActiveOnly(value: boolean): void {
    this.activeOnly.set(value);
    this.rerunCurrentQuery();
  }

  protected toggleBrand(key: string): void {
    this.selectedBrands.update((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    this.rerunCurrentQuery();
  }

  /** Toggle a whole status group: if any of its keys are active, clear them all; otherwise add
   * them all. Keeps the merged "Former"/"Active" chip behaving as a single filter. */
  protected toggleStatusGroup(group: StatusFacetGroup): void {
    this.selectedStatuses.update((prev) => {
      const next = new Set(prev);
      const anySelected = group.keys.some((k) => next.has(k));
      for (const key of group.keys) {
        if (anySelected) next.delete(key); else next.add(key);
      }
      return next;
    });
    this.rerunCurrentQuery();
  }

  /**
   * "Clear filters" inside the search error state — same as the toolbar action, but keeps
   * keyboard focus (the button is destroyed once the re-run turns `loading`).
   * @ticket EB-4580
   */
  protected clearFiltersAfterError(): void {
    this.focusSearchInput();
    this.clearFilters();
  }

  protected clearFilters(): void {
    this.activeOnly.set(false);
    this.selectedBrands.set(new Set());
    this.selectedStatuses.set(new Set());
    this.rerunCurrentQuery();
  }

  protected isBrandSelected(key: string): boolean {
    return this.selectedBrands().has(key);
  }

  protected isStatusGroupSelected(group: StatusFacetGroup): boolean {
    return group.keys.some((key) => this.selectedStatuses().has(key));
  }

  protected statusLabel(key: string): string {
    return STATUS_LABEL[key] ?? key;
  }

  protected facetChipClass(selected: boolean): string {
    const base = 'rounded-full border px-2.5 py-1 text-sm font-medium leading-tight transition-colors';
    return selected
      ? `${base} border-primary/25 bg-primary/10 text-primary`
      : `${base} border-border text-muted-foreground/80 hover:border-foreground/30 hover:text-foreground`;
  }

  protected sortChipClass(mode: CardSortMode): string {
    return this.facetChipClass(this.sortMode() === mode);
  }

  /**
   * Navigate, and tell the operator when the target is the page they are already on.
   * That case is a same-URL navigation, which Angular ignores — without a word the
   * overlay just closes over an unchanged page and the chip reads as broken
   * (EB-4175 follow-up, review M8).
   */
  private navigateOrExplain(commands: (string | number)[]): Promise<boolean> {
    const target = this.router.createUrlTree(commands).toString();
    if (this.currentUrl() === target) {
      this.toastService.open('Already on this page', 'OK', { duration: 3000, tone: 'info' });
      return Promise.resolve(false);
    }
    // Both failure modes, not just the first. A cancelled navigation RESOLVES `false` —
    // authGuard returns false rather than a UrlTree when unauthorized
    // (`auth.guard.ts:17`) — while a guard or resolver that throws REJECTS. Handling only
    // the resolve arm left the rejection as an unhandled promise with no word to the
    // operator: the same "the click did nothing" symptom, moved once more (roundup #4,
    // re-review defect 4).
    return this.router.navigate(commands).then(
      (ok) => {
        if (!ok) this.explainNavigationFailure();
        return ok;
      },
      () => {
        this.explainNavigationFailure();
        return false;
      },
    );
  }

  private explainNavigationFailure(): void {
    this.toastService.open('Could not open that page', 'OK', { duration: 4000, tone: 'error' });
  }

}
